import { Component, OnInit, Output, EventEmitter, Input, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DimensionSettings, type PresetName, STYLE_PRESETS } from '../dimension';

@Component({
  selector: 'fikoyt-ayarlar',
  templateUrl: './ayarlar.component.html',
  styleUrls: ['./ayarlar.component.scss'],
  imports: [CommonModule, FormsModule],
  standalone: true
})
export class AyarlarComponent implements OnInit {

  /** Ayarlar çıkışı */
  @Output() ayarlarDegisti = new EventEmitter<any>();

  /** Ana ayarlar nesnesi */
  @Input() ayarlar: any = {};

  // ===== HASSASIYET AYARLARI =====
  hassasiyet: number = 0.1; // mm
  minOlcum: number = 0.1; // mm
  maxOlcum: number = 10000; // mm
  cemberTolerAnsi: number = 0.05; // % 5
  cemberSapmaEsigi: number = 20; // %
  parallelKontrol: number = 0.001; // derece
  yuzeyMesafeTolerAnsi: number = 0.0001; // mm

  // NOT: Görüntü ayarları artık "Fiko Three.js Ayarlar" → "Ölçüm Ayarları" sekmesinde yönetilmektedir
  // Sadece ölçüm moduna özel basamak ayarları burada kalıyor
  koordinatBas: number = 4;       // nokta koordinatları
  alanBas: number = 10;           // yüzey alanı
  mesafeBas: number = 2;          // cetvel mesafesi
  aciBasamak: number = 1;         // açı

  // ===== ÖLÇÜM BİRİMLERİ =====
  secilenBirim: 'mm' | 'cm' | 'inch' | 'm' = 'mm';
  ondalikSayiBas: number = 2;
  ciBirimFaktore: number = 0.1; // cm faktörü
  inchFaktore: number = 0.03937; // inch faktörü
  mFaktore: number = 0.001; // metre faktörü

  // ===== GEÇMIŞ VE SINIRLAMA =====
  olcumGecmisiLimiti: number = 20;
  planeGeometriBoyu: number = 1000;

  // ===== SEÇİM AYARLARI =====
  secim1Turu: string = 'Her Şey';
  secim2Turu: string = 'Her Şey';
  // Kenar yakalama hassasiyeti (piksel toleransı) — büyük = kenar daha kolay seçilir
  kenarHassasiyet: number = 42;
  // Klasik (eski paralel-çizgili) ölçüm stili açık mı? Açıksa FikoOlcumCizgisi kullanılır.
  klasikOlcumStili: boolean = false;
  secimTurleri = [
    'Her Şey',
    'Sadece Nokta',
    'Sadece Çizgi',
    'Sadece Yüzey',
    'Sadece Gruplar',
    'Nokta Koy'
  ];
  // NOT: Kenar/Nokta seçim ve Raycast ayarları "Fiko Three.js Ayarlar" → "Raycast" sekmesindedir

  // ===== v2.0.0 - GÖRSEL SİSTEM AYARLARI =====
  dimensionPreset: PresetName = 'fikoyt';
  presetListesi: { key: PresetName; label: string }[] = [
    { key: 'catia', label: 'CATIA (Profesyonel)' },
    { key: 'solidworks', label: 'SolidWorks (Mavi)' },
    { key: 'autocad', label: 'AutoCAD (Kırmızı)' },
    { key: 'fikoyt', label: 'FikoYT (Turuncu)' },
    { key: 'minimal', label: 'Minimal (Gri)' }
  ];

  // Animasyon ayarları
  animasyonAktif: boolean = true;
  animasyonSuresi: number = 200; // ms
  fadeInAktif: boolean = true;
  fadeOutAktif: boolean = true;

  // Glow efekt ayarları
  glowAktif: boolean = true;
  glowYogunlugu: number = 0.6;
  glowRengi: string = '#00ffff';
  glowHoverAktif: boolean = true;
  glowSelectAktif: boolean = true;

  // Tooltip ayarları
  tooltipAktif: boolean = true;
  tooltipGecikme: number = 200; // ms
  tooltipArkaplanRengi: string = 'rgba(0,0,0,0.8)';
  tooltipYaziRengi: string = '#ffffff';

  // Ok stili ayarları
  okStili: string = 'FILLED';
  okStilleri = [
    { key: 'FILLED', label: 'Dolu Ok' },
    { key: 'OPEN', label: 'Açık Ok' },
    { key: 'DOT', label: 'Nokta' },
    { key: 'TICK', label: 'Çizgi' },
    { key: 'ARCH', label: 'Yay (CATIA)' },
    { key: 'BOX', label: 'Kutu' },
    { key: 'OBLIQUE', label: 'Eğik Çizgi' },
    { key: 'DOUBLE', label: 'Çift Ok' },
    { key: 'NONE', label: 'Yok' }
  ];

  // Çizgi stili ayarları
  cizgiStili: string = 'SOLID';
  cizgiStilleri = [
    { key: 'SOLID', label: 'Düz' },
    { key: 'DASHED', label: 'Kesikli' },
    { key: 'DOTTED', label: 'Noktalı' },
    { key: 'DASH_DOT', label: 'Çizgi-Nokta' },
    { key: 'CENTER', label: 'Merkez' },
    { key: 'PHANTOM', label: 'Hayalet' }
  ];

  // DimensionSettings referansı
  private dimensionSettings: DimensionSettings;

  // ===== DURUM DEĞİŞKENLERİ =====
  sekmeler = {
    hassasiyet: true,
    birimler: false,
    secim: false,
    gecmis: false,
    gorsel: false  // v2.0.0 - Görsel ayarlar sekmesi
  };

  constructor(public readonly CD: ChangeDetectorRef) {
    // v2.0.0 - DimensionSettings singleton
    this.dimensionSettings = DimensionSettings.getInstance();
  }

  ngOnInit() {
    this.loadAyarlar();
    this.loadGorselAyarlar();
  }

  /**
   * Kaydedilen ayarları yükle
   */
  loadAyarlar() {
    const kaydedilen = localStorage.getItem('FikoYTOlcumAyarlari');
    if (kaydedilen) {
      const parsed = JSON.parse(kaydedilen);
      Object.assign(this, parsed);
      this.CD.detectChanges();
    }
  }

  /**
   * Ayarları kaydet
   */
  kaydetAyarlar() {
    const ayarlarObj = {
      // Hassasiyet
      hassasiyet: this.hassasiyet,
      minOlcum: this.minOlcum,
      maxOlcum: this.maxOlcum,
      cemberTolerAnsi: this.cemberTolerAnsi,
      cemberSapmaEsigi: this.cemberSapmaEsigi,
      parallelKontrol: this.parallelKontrol,
      yuzeyMesafeTolerAnsi: this.yuzeyMesafeTolerAnsi,
      // Basamak ayarları (ölçüm moduna özel)
      koordinatBas: this.koordinatBas,
      alanBas: this.alanBas,
      mesafeBas: this.mesafeBas,
      aciBasamak: this.aciBasamak,
      // Birimler
      secilenBirim: this.secilenBirim,
      ondalikSayiBas: this.ondalikSayiBas,
      ciBirimFaktore: this.ciBirimFaktore,
      inchFaktore: this.inchFaktore,
      mFaktore: this.mFaktore,
      // Geçmiş
      olcumGecmisiLimiti: this.olcumGecmisiLimiti,
      planeGeometriBoyu: this.planeGeometriBoyu,
      // Seçim
      secim1Turu: this.secim1Turu,
      secim2Turu: this.secim2Turu,
      kenarHassasiyet: this.kenarHassasiyet,
      klasikOlcumStili: this.klasikOlcumStili
    };

    localStorage.setItem('FikoYTOlcumAyarlari', JSON.stringify(ayarlarObj));

    // Event emit et
    this.ayarlarDegisti.emit(ayarlarObj);
  }

  /**
   * Varsayılan ayarları geri yükle
   */
  resetAyarlar() {
    // Hassasiyet
    this.hassasiyet = 0.1;
    this.minOlcum = 0.1;
    this.maxOlcum = 10000;
    this.cemberTolerAnsi = 0.05;
    this.cemberSapmaEsigi = 20;
    this.parallelKontrol = 0.001;
    this.yuzeyMesafeTolerAnsi = 0.0001;

    // Basamak ayarları
    this.koordinatBas = 4;
    this.alanBas = 10;
    this.mesafeBas = 2;
    this.aciBasamak = 1;

    // Birimler
    this.secilenBirim = 'mm';
    this.ondalikSayiBas = 2;
    this.ciBirimFaktore = 0.1;
    this.inchFaktore = 0.03937;
    this.mFaktore = 0.001;

    // Geçmiş
    this.olcumGecmisiLimiti = 20;
    this.planeGeometriBoyu = 1000;

    // Seçim
    this.secim1Turu = 'Her Şey';
    this.secim2Turu = 'Her Şey';
    this.kenarHassasiyet = 42;
    this.klasikOlcumStili = false;

    localStorage.removeItem('FikoYTOlcumAyarlari');
    this.CD.detectChanges();

    this.kaydetAyarlar();
  }

  /**
   * Sekme aç/kapa
   */
  toggleSekme(sekme: string) {
    Object.keys(this.sekmeler).forEach(key => {
      this.sekmeler[key] = key === sekme ? !this.sekmeler[key] : false;
    });
  }

  /**
   * Birim dönüştür
   */
  birimDonustur(miktar: number, kaynakBirim: string, hedefBirim: string): number {
    // Önce mm'ye dönüştür
    let mmDegeri = 0;

    switch (kaynakBirim) {
      case 'mm':
        mmDegeri = miktar;
        break;
      case 'cm':
        mmDegeri = miktar * 10;
        break;
      case 'm':
        mmDegeri = miktar * 1000;
        break;
      case 'inch':
        mmDegeri = miktar * 25.4;
        break;
    }

    // Hedef birime dönüştür
    switch (hedefBirim) {
      case 'mm':
        return mmDegeri;
      case 'cm':
        return mmDegeri / 10;
      case 'm':
        return mmDegeri / 1000;
      case 'inch':
        return mmDegeri / 25.4;
      default:
        return mmDegeri;
    }
  }

  /**
   * Değeri seçilen birim cinsinden formatta
   */
  formatBirim(deger: number): string {
    if (deger == null || isNaN(deger)) return `0 ${this.secilenBirim}`;
    return `${deger.toFixed(this.ondalikSayiBas)} ${this.secilenBirim}`;
  }

  // ====================================================================
  // v2.0.0 - GÖRSEL SİSTEM METODLARI
  // ====================================================================

  /**
   * v2.0.0 - Görsel ayarları yükle
   */
  loadGorselAyarlar(): void {
    // LocalStorage'dan yükle
    const kaydedilen = localStorage.getItem('FikoYTDimensionVisualAyarlari');
    if (kaydedilen) {
      try {
        const parsed = JSON.parse(kaydedilen);

        // Preset
        if (parsed.dimensionPreset) this.dimensionPreset = parsed.dimensionPreset;

        // Animasyon
        if (parsed.animasyonAktif !== undefined) this.animasyonAktif = parsed.animasyonAktif;
        if (parsed.animasyonSuresi !== undefined) this.animasyonSuresi = parsed.animasyonSuresi;
        if (parsed.fadeInAktif !== undefined) this.fadeInAktif = parsed.fadeInAktif;
        if (parsed.fadeOutAktif !== undefined) this.fadeOutAktif = parsed.fadeOutAktif;

        // Glow
        if (parsed.glowAktif !== undefined) this.glowAktif = parsed.glowAktif;
        if (parsed.glowYogunlugu !== undefined) this.glowYogunlugu = parsed.glowYogunlugu;
        if (parsed.glowRengi !== undefined) this.glowRengi = parsed.glowRengi;
        if (parsed.glowHoverAktif !== undefined) this.glowHoverAktif = parsed.glowHoverAktif;
        if (parsed.glowSelectAktif !== undefined) this.glowSelectAktif = parsed.glowSelectAktif;

        // Tooltip
        if (parsed.tooltipAktif !== undefined) this.tooltipAktif = parsed.tooltipAktif;
        if (parsed.tooltipGecikme !== undefined) this.tooltipGecikme = parsed.tooltipGecikme;

        // Ok ve çizgi stilleri
        if (parsed.okStili !== undefined) this.okStili = parsed.okStili;
        if (parsed.cizgiStili !== undefined) this.cizgiStili = parsed.cizgiStili;

        // DimensionSettings'e uygula
        this.applyToDimensionSettings();
      } catch (e) {
        console.warn('Görsel ayarlar yüklenemedi:', e);
      }
    }

    this.CD.detectChanges();
  }

  /**
   * v2.0.0 - Görsel ayarları kaydet
   */
  kaydetGorselAyarlar(): void {
    const ayarlarObj = {
      dimensionPreset: this.dimensionPreset,
      animasyonAktif: this.animasyonAktif,
      animasyonSuresi: this.animasyonSuresi,
      fadeInAktif: this.fadeInAktif,
      fadeOutAktif: this.fadeOutAktif,
      glowAktif: this.glowAktif,
      glowYogunlugu: this.glowYogunlugu,
      glowRengi: this.glowRengi,
      glowHoverAktif: this.glowHoverAktif,
      glowSelectAktif: this.glowSelectAktif,
      tooltipAktif: this.tooltipAktif,
      tooltipGecikme: this.tooltipGecikme,
      okStili: this.okStili,
      cizgiStili: this.cizgiStili
    };

    localStorage.setItem('FikoYTDimensionVisualAyarlari', JSON.stringify(ayarlarObj));

    // DimensionSettings'e uygula
    this.applyToDimensionSettings();

    // Event emit et
    this.ayarlarDegisti.emit({
      ...ayarlarObj,
      tip: 'gorsel'
    });
  }

  /**
   * v2.0.0 - Ayarları DimensionSettings'e uygula
   */
  private applyToDimensionSettings(): void {
    // Preset uygula
    this.dimensionSettings.applyPreset(this.dimensionPreset);

    // Renk değerini hex'e çevir
    const glowColor = parseInt(this.glowRengi.replace('#', ''), 16);

    // Ayarları güncelle
    this.dimensionSettings.updateSettings({
      animation: {
        enabled: this.animasyonAktif,
        duration: this.animasyonSuresi,
        fadeIn: this.fadeInAktif,
        fadeOut: this.fadeOutAktif
      },
      glow: {
        enabled: this.glowAktif,
        intensity: this.glowYogunlugu,
        color: glowColor,
        onHover: this.glowHoverAktif,
        onSelect: this.glowSelectAktif
      },
      tooltip: {
        enabled: this.tooltipAktif,
        showDelay: this.tooltipGecikme,
        hideDelay: 100,
        backgroundColor: this.tooltipArkaplanRengi,
        textColor: this.tooltipYaziRengi,
        fontSize: 12
      },
      arrowStyle: this.okStili as any,
      lineStyle: this.cizgiStili as any
    });

    // LocalStorage'a da kaydet
    this.dimensionSettings.saveToLocalStorage();
  }

  /**
   * v2.0.0 - Preset değiştiğinde
   */
  onPresetChange(): void {
    this.dimensionSettings.applyPreset(this.dimensionPreset);
    this.kaydetGorselAyarlar();
  }

  /**
   * v2.0.0 - Görsel ayarları sıfırla
   */
  resetGorselAyarlar(): void {
    this.dimensionPreset = 'fikoyt';
    this.animasyonAktif = true;
    this.animasyonSuresi = 200;
    this.fadeInAktif = true;
    this.fadeOutAktif = true;
    this.glowAktif = true;
    this.glowYogunlugu = 0.6;
    this.glowRengi = '#00ffff';
    this.glowHoverAktif = true;
    this.glowSelectAktif = true;
    this.tooltipAktif = true;
    this.tooltipGecikme = 200;
    this.okStili = 'FILLED';
    this.cizgiStili = 'SOLID';

    localStorage.removeItem('FikoYTDimensionVisualAyarlari');

    // Default preset uygula
    this.dimensionSettings.applyPreset('fikoyt');

    this.CD.detectChanges();
    this.kaydetGorselAyarlar();
  }

  /**
   * v2.0.0 - Ayarları JSON olarak export et
   */
  exportGorselAyarlar(): string {
    return this.dimensionSettings.exportSettings();
  }

  /**
   * v2.0.0 - Ayarları JSON'dan import et
   */
  importGorselAyarlar(json: string): boolean {
    const result = this.dimensionSettings.importSettings(json);
    if (result) {
      this.loadGorselAyarlar();
    }
    return result;
  }
}
